/**
 * usePoolState — reads live pool state from Uniswap V4 StateView contract.
 *
 * Computes poolId from pool key and reads slot0 (sqrtPriceX96, tick, fees)
 * and liquidity via publicClient.readContract.
 *
 * ARCH-27: Contract interaction hook pattern (useQuery with polling)
 * Story 5.1 Task 3
 */

import { useQuery } from "@tanstack/react-query";
import { getPublicClient } from "wagmi/actions";

import { stateViewAbi } from "../../lib/abis/state-view";
import {
	CLANKER_TICK_SPACING,
	DYNAMIC_FEE_FLAG,
	getChainAddresses,
} from "../../lib/chain-addresses";
import { toCheckedHex } from "../../lib/hex-utils";
import { computePoolId } from "../../lib/swap-math";
import { getActiveChain, wagmiConfig } from "../../lib/wagmi-config";

export type PoolState = {
	sqrtPriceX96: bigint;
	tick: number;
	protocolFee: number;
	lpFee: number;
	liquidity: bigint;
};

/**
 * Order tokens and compute pool ID using shared swap-math utility.
 */
function computePoolIdFromAddresses(
	tokenAddress: string,
	hooksAddress: string,
	wethAddress: string,
	tickSpacing: number
): `0x${string}` {
	const tokenHex = toCheckedHex(tokenAddress);
	const wethHex = toCheckedHex(wethAddress);
	const token0 = tokenHex < wethHex ? tokenHex : wethHex;
	const token1 = tokenHex < wethHex ? wethHex : tokenHex;

	return computePoolId(token0, token1, DYNAMIC_FEE_FLAG, tickSpacing, toCheckedHex(hooksAddress));
}

async function fetchPoolState(tokenAddress: string, poolAddress: string): Promise<PoolState> {
	const activeChain = getActiveChain();
	const publicClient = getPublicClient(wagmiConfig, { chainId: activeChain.id });

	if (!publicClient) {
		// eslint-disable-next-line no-restricted-syntax -- TanStack Query queryFn requires thrown errors
		throw new Error("Public client unavailable");
	}

	const addresses = getChainAddresses(activeChain.id);
	if (!addresses) {
		// eslint-disable-next-line no-restricted-syntax -- TanStack Query queryFn requires thrown errors
		throw new Error(`Unsupported chain: ${activeChain.id}`);
	}

	// poolAddress is the hooks contract address
	const poolId = computePoolIdFromAddresses(
		tokenAddress,
		poolAddress,
		addresses.weth,
		CLANKER_TICK_SPACING
	);

	const [slot0Result, liquidityResult] = await Promise.all([
		publicClient.readContract({
			address: addresses.stateView,
			abi: stateViewAbi,
			functionName: "getSlot0",
			args: [poolId],
		}),
		publicClient.readContract({
			address: addresses.stateView,
			abi: stateViewAbi,
			functionName: "getLiquidity",
			args: [poolId],
		}),
	]);

	return {
		sqrtPriceX96: slot0Result[0],
		tick: slot0Result[1],
		protocolFee: slot0Result[2],
		lpFee: slot0Result[3],
		liquidity: liquidityResult,
	};
}

type UsePoolStateReturn = {
	sqrtPriceX96: bigint | undefined;
	tick: number | undefined;
	protocolFee: number | undefined;
	lpFee: number | undefined;
	liquidity: bigint | undefined;
	isLoading: boolean;
	error: Error | null;
	refetch: () => void;
};

/**
 * Hook to read live pool state from StateView contract.
 *
 * @param tokenAddress - The ERC-20 token contract address
 * @param poolAddress - The hooks contract address (from token detail API)
 * @returns Pool state with sqrtPriceX96, tick, liquidity, fees, loading/error states
 */
export function usePoolState(tokenAddress: string, poolAddress: string): UsePoolStateReturn {
	const enabled = Boolean(tokenAddress) && Boolean(poolAddress);

	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["pool-state", tokenAddress, poolAddress],
		queryFn: () => fetchPoolState(tokenAddress, poolAddress),
		enabled,
		refetchInterval: 12_000, // Every ~1 block
		staleTime: 10_000,
	});

	return {
		sqrtPriceX96: data?.sqrtPriceX96,
		tick: data?.tick,
		protocolFee: data?.protocolFee,
		lpFee: data?.lpFee,
		liquidity: data?.liquidity,
		isLoading: enabled ? isLoading : false,
		error: error instanceof Error ? error : null,
		refetch,
	};
}
